import { Date, DateTime, DateTimeService, DateTimeUtils, TimeUnitUtils } from '@mews/framework';
import { StructuredDate } from '@optimus-web/core';
import { TimeUnit } from '@mews/enums';

export const getAvailabilityBlockAdjustedEnd = ({
    endDateTime,
    timeUnit,
    isOverlappingTimeUnit,
}: {
    endDateTime: DateTime;
    timeUnit: TimeUnit;
    isOverlappingTimeUnit: boolean;
}) => {
    const timePart = TimeUnitUtils.timeUnitToDateTimePart(timeUnit);

    return (timeUnit === TimeUnit.Day || timeUnit === TimeUnit.Month) && !isOverlappingTimeUnit
        ? DateTimeService.endOf(TimeUnitUtils.timeUnitToLuxonTimePart(timeUnit))(endDateTime)
        : TimeUnitUtils.getNextTimeUnitStart(endDateTime, timePart);
};

type GetDateConstraints<R> = (
    startUtc: string | undefined,
    endUtc: string | undefined,
    timezone: string | undefined,
    isOverlappingTimeUnit: boolean,
    timeUnit: TimeUnit | undefined
) => {
    startDate: R | null;
    endDate: R | null;
};

export const getAvailabilityBlockDateConstraints: GetDateConstraints<Date> = (
    startUtc,
    endUtc,
    timezone,
    isOverlappingTimeUnit,
    timeUnit
) => {
    if (!startUtc || !endUtc || !timezone || !timeUnit) {
        return {
            startDate: null,
            endDate: null,
        };
    }
    const startDateTime = DateTimeService.fromUtcString(startUtc, timezone);

    const endDateTime = DateTimeService.fromUtcString(endUtc, timezone);

    const startDate = DateTimeService.toDate(startDateTime);

    const endDate = DateTimeService.toDate(
        getAvailabilityBlockAdjustedEnd({ endDateTime, timeUnit, isOverlappingTimeUnit })
    );

    return {
        startDate,
        endDate,
    };
};

export const getAvailabilityBlockStructuredDateConstraints: GetDateConstraints<StructuredDate> = (
    startUtc,
    endUtc,
    timezone,
    isOverlappingTimeUnit,
    timeUnit
) => {
    const { startDate, endDate } = getAvailabilityBlockDateConstraints(
        startUtc,
        endUtc,
        timezone,
        isOverlappingTimeUnit,
        timeUnit
    );

    if (!startDate || !endDate) {
        return {
            startDate: null,
            endDate: null,
        };
    }

    return {
        startDate: DateTimeUtils.dateToStructuredDate(startDate),
        endDate: DateTimeUtils.dateToStructuredDate(endDate),
    };
};
